import { useEffect, useLayoutEffect, useRef } from "preact/hooks";
import { strings } from "../i18n/strings.es-AR.js";
import { messages, sending, lastBotMessage, phase, hasMoreHistory, loadingOlder } from "../state/store.js";
import { loadOlderMessages } from "../hooks/useConversation.js";
import { MessageBubble } from "./MessageBubble.jsx";
import { QuickReplyChips } from "./QuickReplyChips.jsx";
import { ListPicker } from "./ListPicker.jsx";
import { CsatInline } from "./CsatInline.jsx";
import { TypingIndicator } from "./TypingIndicator.jsx";

// Cuánto puede estar el hilo lejos del fondo y que un cambio de alto del
// contenedor igual lo vuelva a pegar abajo. Tiene que cubrir el chrome que monta
// tarde (barra de acciones + composer ≈ 105 px, banners ≈ 50) sin secuestrar al
// usuario que subió a leer la conversación.
const PIN_THRESHOLD_PX = 400;

// Arriba de esto el salto se hace instantáneo en vez de animado. Dos razones:
// animar cientos de píxeles al hidratar el historial no aporta nada, y sobre todo
// un scroll suave en vuelo se lleva puesta la corrección — mientras la animación
// avanza hacia un destino ya viejo, el ResizeObserver ve una distancia al fondo
// enorme y no re-pega. Con el salto instantáneo cada cambio de alto arranca desde
// una posición exacta.
const INSTANT_JUMP_PX = 240;

// A qué distancia del tope se pide la página siguiente. Un umbral y no el borde
// exacto: con scroll por inercia el usuario pasa de 300 px a 0 en un frame, y
// pedir recién en 0 le muestra el tope vacío mientras viaja la request.
const LOAD_OLDER_THRESHOLD_PX = 300;

const dayFormatter = new Intl.DateTimeFormat("es-AR", { day: "numeric", month: "long", year: "numeric" });

function dayKey(ts) {
  const d = new Date(ts || Date.now());
  return `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
}

/**
 * Etiqueta del separador de día. Con el historial del abonado el hilo cruza
 * meses y dispositivos: sin una fecha cada tanto, los tramos viejos parecen
 * parte de la conversación de hoy.
 */
function dayLabel(ts) {
  const date = new Date(ts || Date.now());
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(today.getDate() - 1);

  if (dayKey(date) === dayKey(today)) return strings.today;
  if (dayKey(date) === dayKey(yesterday)) return strings.yesterday;
  return dayFormatter.format(date);
}

export function MessageList() {
  const scrollerRef = useRef(null);
  // Alto y posición justo antes de prepender una página: sin esto el contenido
  // nuevo empuja el hilo hacia abajo y el usuario pierde el punto donde estaba.
  const anchorRef = useRef(null);
  // Consumida por el efecto de auto-scroll: un prepend cambia items.length
  // igual que un mensaje nuevo, y sin la marca el hilo saltaría al fondo cada
  // vez que se carga historial.
  const prependedRef = useRef(false);
  const items = messages.value;
  const last = lastBotMessage.value;
  // Al abrir con hilo previo, la conexión (y la bienvenida que puede traer)
  // también es una espera: el "Conectando…" de abajo sólo cubre el hilo vacío.
  // Los chips del último mensaje se esconden igual que mientras responde el bot.
  const waiting = sending.value || (phase.value === "booting" && items.length > 0);

  function requestOlder() {
    const el = scrollerRef.current;
    if (!el || !hasMoreHistory.value || loadingOlder.value) return;
    anchorRef.current = { scrollHeight: el.scrollHeight, scrollTop: el.scrollTop };
    loadOlderMessages().then((added) => {
      if (!added) anchorRef.current = null;
    });
  }

  function onScroll(event) {
    if (event.currentTarget.scrollTop <= LOAD_OLDER_THRESHOLD_PX) requestOlder();
  }

  // Compensación del prepend, antes de pintar: el hilo queda exactamente donde
  // estaba y lo nuevo aparece arriba. En useLayoutEffect y no en useEffect
  // porque un frame intermedio con la posición vieja se ve como un salto.
  useLayoutEffect(() => {
    const el = scrollerRef.current;
    const anchor = anchorRef.current;
    if (!el || !anchor) return;
    anchorRef.current = null;
    const grew = el.scrollHeight - anchor.scrollHeight;
    if (grew > 0) {
      el.scrollTop = anchor.scrollTop + grew;
      prependedRef.current = true;
    }
  }, [items.length]);

  // Pegar el hilo al final.
  //
  // Se scrollea el CONTENEDOR y no un centinela con `scrollIntoView`: el
  // centinela vive por dentro del `py-4` del hilo, así que alinear su borde con
  // el del viewport deja ese padding fuera de vista.
  //
  // El rAF tampoco es decorativo. El chrome de abajo (barra de acciones,
  // composer) y los banners de arriba montan DESPUÉS del primer lote de
  // mensajes, y cada uno le saca altura al scroller. Medido en Chrome: el
  // contenedor pasó de 629 px a 584 y después a 536 mientras el scroll ya estaba
  // en curso, y quedaba 108 px corto — exactamente el alto de la barra más el
  // composer, con la última burbuja tapada.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    // El crecimiento vino de arriba (historial), no de un mensaje nuevo: la
    // posición ya la fijó el layout effect.
    if (prependedRef.current) {
      prependedRef.current = false;
      return;
    }
    const raf = requestAnimationFrame(() => {
      const distance = el.scrollHeight - el.clientHeight - el.scrollTop;
      el.scrollTo({
        top: el.scrollHeight,
        behavior: distance > INSTANT_JUMP_PX ? "auto" : "smooth"
      });
    });
    return () => cancelAnimationFrame(raf);
  }, [items.length, waiting]);

  // Y si el scroller cambia de alto MÁS TARDE —el chrome que monta en otro
  // commit, el teclado del celular, una fuente que termina de cargar—, se vuelve
  // a pegar al instante. Sólo cuando se encoge y el usuario ya estaba mirando el
  // final: si subió a leer el historial, no se le mueve la vista.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || typeof ResizeObserver === "undefined") return;

    let previousHeight = el.clientHeight;
    const observer = new ResizeObserver(() => {
      const shrank = el.clientHeight < previousHeight;
      const distanceToBottom = el.scrollHeight - el.clientHeight - el.scrollTop;
      previousHeight = el.clientHeight;
      if (shrank && distanceToBottom > 0 && distanceToBottom < PIN_THRESHOLD_PX) {
        el.scrollTop = el.scrollHeight;
      }
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  // Si el hilo entra entero en la pantalla no hay scroll que disparar el
  // pedido, y el historial quedaría inalcanzable: se pide hasta llenar el
  // viewport. `items.length` como dependencia encadena las páginas.
  useEffect(() => {
    const el = scrollerRef.current;
    if (!el || !hasMoreHistory.value || loadingOlder.value) return;
    if (el.scrollHeight <= el.clientHeight) requestOlder();
  }, [items.length, hasMoreHistory.value, loadingOlder.value]);

  let previousDay = null;

  return (
    <main ref={scrollerRef} onScroll={onScroll} class="flex-1 overflow-y-auto">
      {/* min-h-full + justify-end: con pocos mensajes la conversación se apoya
          sobre el composer en vez de dejar un hueco enorme abajo (se nota en
          desktop, donde la app ocupa toda la pantalla). Si el hilo crece, la
          caja se estira y scrollea normal. */}
      <div class="chat-col flex min-h-full flex-col justify-end space-y-2 px-3 py-4 md:px-5">
      {phase.value === "booting" && items.length === 0 && (
        <p class="py-8 text-center text-xs text-white/60">{strings.connecting}</p>
      )}

      {loadingOlder.value && (
        <p class="py-2 text-center text-xs text-white/60">{strings.loadingHistory}</p>
      )}

      {items.map((m) => {
        const day = dayKey(m.ts);
        const startsDay = day !== previousDay;
        previousDay = day;
        return (
          <div key={m.key}>
            {startsDay && (
              <p class="py-2 text-center text-[11px] uppercase tracking-wide text-white/50">
                {dayLabel(m.ts)}
              </p>
            )}
            <MessageBubble message={m} />
            {m.csat && <CsatInline message={m} />}
            {m === last && !waiting && (
              <>
                <QuickReplyChips message={m} />
                <ListPicker message={m} />
              </>
            )}
          </div>
        );
      })}

      {waiting && <TypingIndicator />}
      </div>
    </main>
  );
}
